import { FieldValues } from 'react-hook-form';

// Helper function to safely access nested properties
const getNestedValue = (obj: Record<string, unknown>, path: string) => {
  return path.split('.').reduce((current, key) => {
    return current &&
      typeof current === 'object' &&
      current !== null &&
      key in current
      ? (current as Record<string, unknown>)[key]
      : undefined;
  }, obj);
};

// Overload for simple arrays (no field name needed)
export function getSortedList<T>(data: T[], order?: 'asc' | 'desc'): T[];

// Overload for object arrays (with field name)
export function getSortedList<TSchema extends FieldValues>(
  data: TSchema[],
  fieldNameToOrderAgainst: string | keyof TSchema,
  order?: 'asc' | 'desc',
): TSchema[];

// Implementation
export function getSortedList<T extends FieldValues>(
  data: T[],
  fieldNameOrOrder?: string | keyof T | 'asc' | 'desc',
  order: 'asc' | 'desc' = 'desc',
): T[] {
  return data?.slice().sort((a, b) => {
    let aValue: unknown;
    let bValue: unknown;
    let sortOrder: 'asc' | 'desc';

    // Check if second parameter is a field name or order
    if (
      typeof fieldNameOrOrder === 'string' &&
      fieldNameOrOrder !== 'asc' &&
      fieldNameOrOrder !== 'desc'
    ) {
      // Object array with field name
      aValue = getNestedValue(a as Record<string, unknown>, fieldNameOrOrder);
      bValue = getNestedValue(b as Record<string, unknown>, fieldNameOrOrder);
      sortOrder = order;
    } else {
      // Simple array - use values directly
      aValue = a;
      bValue = b;
      sortOrder = (fieldNameOrOrder as 'asc' | 'desc') || 'desc';
    }

    // Check if the field values are valid date strings
    const dateA = new Date(aValue as string | number | Date);
    const dateB = new Date(bValue as string | number | Date);

    // If both are valid dates, sort by date
    if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
      return sortOrder === 'asc'
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    }

    // Otherwise, sort alphabetically for strings
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    // Fallback for other types or missing values
    if (!aValue) return 1;
    if (!bValue) return -1;
    return 0;
  });
}
